import { Link } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  Gem,
  Landmark,
  FileCheck2,
  Scale,
  BadgeIndianRupee,
  ClipboardCheck,
  Zap,
  Mail,
  MapPin,
  Star,
  ShieldCheck,
  Timer,
} from "lucide-react";
import { BRAND, waLink, telLink, telLinkPrimary } from "@/lib/brand";
import BrandStamp from "@/components/BrandStamp";
import usePageMeta from "@/hooks/usePageMeta";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/b35829a5-a4f8-4d7e-a662-70f82d3fda77/images/0cab31912acf7a87fbbf1d2808a7d7b7ff4f5aaa6f0b4c9af4c9338824a49f84.jpeg";
const PROCESS_IMG =
  "https://static.prod-images.emergentagent.com/jobs/b35829a5-a4f8-4d7e-a662-70f82d3fda77/images/44358f05eca453838715db9c27a93c6db5a2e894016fd73ac6a1cf650bdf7380.jpeg";

const EMAIL = "acca.vishaltaneja@gmail.com";

const PROCESS = [
  {
    n: "01",
    icon: Gem,
    title: "Bring Your Jewellery & Documents",
    body: "Bring your bank-mortgaged jewellery and the relevant loan documents to our branch — or call us for a home visit.",
  },
  {
    n: "02",
    icon: Landmark,
    title: "Bank & Mortgage Verification",
    body: "We verify the bank and mortgage details end-to-end, so everything is fully legal and transparent.",
  },
  {
    n: "03",
    icon: Scale,
    title: "Purity, Weight & Value Check",
    body: "Our experts check the jewellery's purity, weight and value — live, right in front of you.",
  },
  {
    n: "04",
    icon: BadgeIndianRupee,
    title: "Quick & Competitive Valuation",
    body: "Get an on-the-spot valuation at the best possible market rate. No hidden cuts, no games.",
  },
  {
    n: "05",
    icon: ClipboardCheck,
    title: "Complete the Formalities",
    body: "We handle the required paperwork and bank formalities for a smooth, hassle-free release.",
  },
  {
    n: "06",
    icon: Zap,
    title: "Fast & Transparent Service",
    body: "Receive fast, transparent service with payment in cash or bank transfer — same day.",
  },
];

const WA_MSG =
  "Namaste! Meri jewellery bank mein mortgaged hai. Kripya buy back process aur best rate ke baare mein bataye. (Top Rate)";

export default function BankMortgagedJewelleryPage() {
  usePageMeta({
    title:
      "Bank Mortgaged Jewellery Buy Back in Gurgaon | Release Pledged Jewellery — Top Rate",
    description:
      "Need to release your bank-mortgaged jewellery? Top Rate offers quick service and the best rates for bank-pledged gold jewellery in Gurgaon, Delhi, Ghaziabad, Noida & nearby areas. Call 8796-432-666.",
    path: "/bank-mortgaged-jewellery",
    keywords:
      "bank mortgaged jewellery buy back, release pledged jewellery gurgaon, bank mortgage gold release, sell mortgaged jewellery, girvi jewellery buyer gurgaon",
  });

  return (
    <div>
      {/* HERO */}
      <section
        data-testid="bank-jewellery-hero"
        className="relative overflow-hidden bg-[#FAF6EC]"
      >
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7 tr-fade-up">
              <div className="inline-flex items-center gap-2 border border-[#641C24]/40 bg-[#FFFDF8] text-[#641C24] px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold">
                <Gem size={13} strokeWidth={2} />
                Bank-Mortgaged Jewellery · बैंक में गिरवी ज्वेलरी
              </div>

              <h1 className="font-display text-[#2D181A] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.98] mt-6">
                Need to release your{" "}
                <span className="italic text-[#B8860B]">
                  bank-mortgaged jewellery?
                </span>
              </h1>

              <p className="mt-6 text-base sm:text-lg text-[#4A3B3C] leading-relaxed max-w-2xl">
                We provide quick service and the{" "}
                <span className="font-semibold text-[#2D181A]">
                  best possible rates
                </span>{" "}
                for your jewellery. From bank verification to final payout —
                everything is handled by our govt-approved valuers,
                transparently and fast.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] sm:text-xs uppercase tracking-[0.22em] font-bold text-[#641C24]">
                <span className="inline-flex items-center gap-1.5">
                  <Star size={13} className="text-[#D4AF37] fill-[#D4AF37]" />
                  Quick Service
                </span>
                <span className="text-[#D4AF37]">•</span>
                <span className="inline-flex items-center gap-1.5">
                  <BadgeIndianRupee size={13} className="text-[#D4AF37]" />
                  Best Rates
                </span>
                <span className="text-[#D4AF37]">•</span>
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck size={13} className="text-[#D4AF37]" />
                  Trusted Process
                </span>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={telLinkPrimary}
                  data-testid="bank-jewellery-hero-call"
                  className="inline-flex items-center justify-center gap-2 bg-[#641C24] hover:bg-[#4A151A] text-[#FFFDF8] px-7 py-4 text-sm font-semibold uppercase tracking-wider"
                >
                  <Phone size={17} /> Call {BRAND.phonePrimary}
                </a>
                <a
                  href={waLink(WA_MSG)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="bank-jewellery-hero-whatsapp"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FBA57] text-white px-7 py-4 text-sm font-semibold uppercase tracking-wider"
                >
                  <MessageCircle size={17} /> WhatsApp Us
                </a>
              </div>

              <div className="mt-5 text-sm text-[#4A3B3C]">
                Also on{" "}
                <a
                  href={telLink}
                  className="font-semibold text-[#641C24] underline decoration-[#D4AF37] underline-offset-4"
                >
                  {BRAND.phone}
                </a>{" "}
                ·{" "}
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="bank-jewellery-email"
                  className="font-semibold text-[#641C24] underline decoration-[#D4AF37] underline-offset-4"
                >
                  {EMAIL}
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative">
                <img
                  src={HERO_IMG}
                  alt="Bank-mortgaged gold jewellery with loan documents — buy back at Top Rate"
                  loading="eager"
                  className="w-full h-72 md:h-96 object-cover shadow-[0_18px_44px_-14px_rgba(45,24,26,0.4)]"
                />
                <div className="absolute -top-4 -left-3 bg-[#641C24] text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.24em] px-4 py-2 shadow-lg">
                  <Timer size={11} className="inline mr-1.5 -mt-0.5" />
                  Quick Release
                </div>
                <div className="absolute -bottom-4 -right-3 bg-[#D4AF37] text-[#2D181A] text-[10px] font-bold uppercase tracking-[0.24em] px-4 py-2 shadow-lg">
                  Best Possible Rates
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandStamp
        variant="gold"
        tagline={<>Quick Service · Best Rates · Trusted Process</>}
      />

      {/* PROCESS */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
              Our Process · हमारी प्रक्रिया
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05]">
              Six simple steps to{" "}
              <span className="italic text-[#641C24]">
                release your jewellery
              </span>
              .
            </h2>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {PROCESS.map((s, i) => (
                <div
                  key={s.n}
                  data-testid={`bank-jewellery-step-${i}`}
                  className="bg-white border border-[#E5DCC5] p-6 shadow-sm hover:shadow-md transition-shadow relative"
                >
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 border border-[#D4AF37] flex items-center justify-center text-[#641C24]">
                      <s.icon size={18} strokeWidth={1.7} />
                    </div>
                    <div className="font-display text-4xl font-black text-[#D4AF37]/25 leading-none">
                      {s.n}
                    </div>
                  </div>
                  <h3 className="font-display text-lg font-bold mt-4 leading-tight">
                    {s.title}
                  </h3>
                  <p className="text-sm text-[#4A3B3C] leading-relaxed mt-2">
                    {s.body}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-40">
            <img
              src={PROCESS_IMG}
              alt="Jewellery being released back to customer across a bank counter"
              loading="lazy"
              className="w-full object-cover shadow-[0_18px_44px_-14px_rgba(45,24,26,0.35)]"
            />
            <div className="bg-[#641C24] text-[#FFFDF8] p-6 md:p-8 -mt-1">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                Why Top Rate
              </div>
              <p className="text-sm leading-relaxed mt-3 text-[#FFFDF8]/90">
                Govt-approved valuers, live purity testing, direct bank
                coordination and same-day payout — that's why families across
                Gurgaon and Delhi NCR trust Top Rate with their bank-mortgaged
                jewellery.
              </p>
              <Link
                to="/mortgaged-gold"
                className="inline-block mt-4 text-[11px] uppercase tracking-[0.22em] font-semibold text-[#D4AF37] hover:text-white"
              >
                → Girvi Sona Buyback Details
              </Link>
            </div>
          </div>
        </div>
      </section>

      <BrandStamp
        variant="dark"
        tagline={<>Serving Gurgaon · Delhi · Ghaziabad · Noida &amp; Nearby Areas</>}
      />

      {/* CONTACT BAND */}
      <section
        data-testid="bank-jewellery-contact"
        className="bg-[#2D181A] text-[#FFFDF8]"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
              <Star size={13} className="fill-[#D4AF37]" />
              Quick Service • Best Rates • Trusted Process
              <Star size={13} className="fill-[#D4AF37]" />
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-5 leading-[1.05]">
              Release your jewellery{" "}
              <span className="italic text-[#D4AF37]">today</span>.
            </h2>

            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={telLinkPrimary}
                data-testid="bank-jewellery-cta-call-primary"
                className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B8860B] text-[#2D181A] px-8 py-4 text-sm font-bold uppercase tracking-wider"
              >
                <Phone size={17} /> {BRAND.phonePrimary}
              </a>
              <a
                href={telLink}
                data-testid="bank-jewellery-cta-call-secondary"
                className="inline-flex items-center gap-2 border border-[#D4AF37] hover:bg-[#D4AF37]/10 text-[#D4AF37] px-8 py-4 text-sm font-bold uppercase tracking-wider"
              >
                <Phone size={17} /> 9810107276
              </a>
              <a
                href={waLink(WA_MSG)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bank-jewellery-cta-whatsapp"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FBA57] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider"
              >
                <MessageCircle size={17} /> WhatsApp
              </a>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-x-8 gap-y-3 text-sm text-[#FFFDF8]/85">
              <a
                href={`mailto:${EMAIL}`}
                className="inline-flex items-center gap-2 hover:text-[#D4AF37]"
              >
                <Mail size={15} className="text-[#D4AF37]" /> {EMAIL}
              </a>
              <span className="inline-flex items-center gap-2">
                <MapPin size={15} className="text-[#D4AF37]" /> Gurgaon • Delhi
                • Ghaziabad • Noida &amp; Nearby Areas
              </span>
            </div>
          </div>
        </div>
      </section>

      <BrandStamp
        variant="gold"
        tagline={<>Top Rate · Sona Do Paisa Lo · Best Rate for Your Jewellery</>}
      />
    </div>
  );
}
